/**
 * iCal (.ics) file export utility
 * Generates downloadable .ics files for events
 * Works on all platforms (web, iOS, Android)
 */

import { format, parseISO } from 'date-fns';

/**
 * Generate iCal date format (YYYYMMDD or YYYYMMDDTHHmmss)
 */
const formatICalDate = (dateStr, timeStr = null) => {
  if (!dateStr) return null;
  
  const datePart = dateStr.replace(/-/g, '');
  
  if (timeStr) {
    const timePart = timeStr.replace(/:/g, '') + '00';
    return `${datePart}T${timePart}`;
  }
  
  return datePart;
};

/**
 * Escape special characters for iCal format
 */
const escapeICalText = (text) => {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
};

/**
 * Generate a unique UID for the event
 */
const generateUID = (event) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `${event.id || timestamp}-${random}@my-mind.me`;
};

/**
 * Generate iCal content for a single event
 */
export const generateICalEvent = (event) => {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//mind. Habit Tracker//my-mind.me//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
  ];

  // UID
  lines.push(`UID:${generateUID(event)}`);

  // Timestamp
  const now = new Date();
  const dtstamp = format(now, "yyyyMMdd'T'HHmmss'Z'");
  lines.push(`DTSTAMP:${dtstamp}`);

  // Event dates
  const startDate = event.startDate || event.date;
  const endDate = event.endDate || startDate;

  if (event.startTime && event.endTime) {
    // Timed event
    const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
    lines.push(`DTSTART;TZID=${tzid}:${formatICalDate(startDate, event.startTime)}`);
    lines.push(`DTEND;TZID=${tzid}:${formatICalDate(endDate, event.endTime)}`);
  } else if (event.startTime) {
    // Has start time but no end time - default to 1 hour
    const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
    lines.push(`DTSTART;TZID=${tzid}:${formatICalDate(startDate, event.startTime)}`);
    
    // Calculate end time (1 hour later)
    const [hours, minutes] = event.startTime.split(':');
    const endHour = (parseInt(hours) + 1) % 24;
    const endTime = `${String(endHour).padStart(2, '0')}:${minutes}`;
    lines.push(`DTEND;TZID=${tzid}:${formatICalDate(endDate, endTime)}`);
  } else {
    // All-day event
    lines.push(`DTSTART;VALUE=DATE:${formatICalDate(startDate)}`);
    // For all-day events, DTEND should be the day after
    const endDateObj = parseISO(endDate);
    endDateObj.setDate(endDateObj.getDate() + 1);
    lines.push(`DTEND;VALUE=DATE:${format(endDateObj, 'yyyyMMdd')}`);
  }

  // Summary (title)
  lines.push(`SUMMARY:${escapeICalText(event.name)}`);

  // Description (optional)
  if (event.description) {
    lines.push(`DESCRIPTION:${escapeICalText(event.description)}`);
  }

  // Location (optional)
  if (event.location) {
    lines.push(`LOCATION:${escapeICalText(event.location)}`);
  }

  // Recurrence (if applicable)
  if (event.recurrence && event.recurrence !== 'none') {
    const rrule = generateRRule(event.recurrence);
    if (rrule) {
      lines.push(rrule);
    }
  }

  lines.push('END:VEVENT');
  lines.push('END:VCALENDAR');

  return lines.join('\r\n');
};

/**
 * Generate RRULE for recurring events
 */
const generateRRule = (recurrence) => {
  switch (recurrence) {
    case 'daily':
      return 'RRULE:FREQ=DAILY';
    case 'weekly':
      return 'RRULE:FREQ=WEEKLY';
    case 'biweekly':
      return 'RRULE:FREQ=WEEKLY;INTERVAL=2';
    case 'monthly':
      return 'RRULE:FREQ=MONTHLY';
    case 'yearly':
      return 'RRULE:FREQ=YEARLY';
    default:
      return null;
  }
};

/**
 * Generate iCal content for multiple events
 */
export const generateICalMultiple = (events) => {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//mind. Habit Tracker//my-mind.me//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  events.forEach((event) => {
    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${generateUID(event)}`);
    
    const now = new Date();
    lines.push(`DTSTAMP:${format(now, "yyyyMMdd'T'HHmmss'Z'")}`);

    const startDate = event.startDate || event.date;
    const endDate = event.endDate || startDate;

    if (event.startTime && event.endTime) {
      const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
      lines.push(`DTSTART;TZID=${tzid}:${formatICalDate(startDate, event.startTime)}`);
      lines.push(`DTEND;TZID=${tzid}:${formatICalDate(endDate, event.endTime)}`);
    } else if (event.startTime) {
      const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
      lines.push(`DTSTART;TZID=${tzid}:${formatICalDate(startDate, event.startTime)}`);
      const [hours, minutes] = event.startTime.split(':');
      const endHour = (parseInt(hours) + 1) % 24;
      const endTime = `${String(endHour).padStart(2, '0')}:${minutes}`;
      lines.push(`DTEND;TZID=${tzid}:${formatICalDate(endDate, endTime)}`);
    } else {
      lines.push(`DTSTART;VALUE=DATE:${formatICalDate(startDate)}`);
      const endDateObj = parseISO(endDate);
      endDateObj.setDate(endDateObj.getDate() + 1);
      lines.push(`DTEND;VALUE=DATE:${format(endDateObj, 'yyyyMMdd')}`);
    }

    lines.push(`SUMMARY:${escapeICalText(event.name)}`);
    
    if (event.description) {
      lines.push(`DESCRIPTION:${escapeICalText(event.description)}`);
    }
    
    if (event.location) {
      lines.push(`LOCATION:${escapeICalText(event.location)}`);
    }

    if (event.recurrence && event.recurrence !== 'none') {
      const rrule = generateRRule(event.recurrence);
      if (rrule) lines.push(rrule);
    }

    lines.push('END:VEVENT');
  });

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
};

/**
 * Download iCal file for a single event
 */
export const downloadICalEvent = (event) => {
  const icalContent = generateICalEvent(event);
  const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.name.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Download iCal file for multiple events
 */
export const downloadICalMultiple = (events, filename = 'events') => {
  const icalContent = generateICalMultiple(events);
  const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Generate a webcal:// URL for subscribing to a calendar feed
 * Note: This requires a server endpoint to serve the .ics file
 */
export const generateWebcalUrl = (userId) => {
  // This would point to a Cloud Function that serves the user's calendar
  return `webcal://us-central1-mind-habit-tracker.cloudfunctions.net/getCalendarFeed?userId=${userId}`;
};
